/**
 * Design constants for Assessment / Child Profile screens
 * Colors and spacing values from Figma design
 */

// --- Stage Badge Color System ---

export const STAGE_COLORS = {
  thriving: {
    badgeBg: '#55A00F',
    cardBg: '#ECF7EA',
    text: '#55A00F',
    label: 'THRIVING',
  },
  growing: {
    badgeBg: '#F77F3A',
    cardBg: '#FFE4D4',
    text: '#F77F3A',
    label: 'GROWING',
  },
  building: {
    badgeBg: '#4D7CE1',
    cardBg: '#E3ECFF',
    text: '#4D7CE1',
    label: 'BUILDING',
  },
  notAssessed: {
    badgeBg: '#A9B7BE',
    cardBg: '#EDF0F5',
    text: '#4D5762',
    label: 'needs check-in',
  },
} as const;

// --- Profile Screen Colors ---

export const PROFILE_COLORS = {
  // Primary
  primaryBlue: '#01A7C8',
  darkBlue: '#007CAD',
  lightestBlue: '#DBF7F8',

  // Grayscale
  textPrimary: '#1B2831',
  textSecondary: '#4D5762',
  textTertiary: '#323B46',
  textMuted: '#A9B7BE',

  // Surfaces
  white: '#FFFFFF',
  cardBg: '#FFFFFF',
  sectionBg: '#EDF0F5',

  // Child info pill
  childPillBg: '#007CAD',
  childPillText: '#FFFFFF',
  childPillAge: '#DBF7F8',

  // Add child button
  addChildBorder: '#58C5DA',
  addChildIconBg: '#DBF7F8',
  addChildText: '#01A7C8',
} as const;

// --- Spacing ---

export const PROFILE_SPACING = {
  cardPadding: 16,
  cardGap: 8,
  sectionGap: 16,
  buttonHeight: 48,
} as const;

// --- Radius ---

export const PROFILE_RADIUS = {
  card: 16,
  badge: 12,
  button: 12,
  childPill: 48,
  avatar: 30, // half of 59px (round avatar)
  categoryCard: 16,
} as const;

// --- Shadows ---

export const PROFILE_SHADOWS = {
  card: '0px 0px 16px rgba(0, 124, 173, 0.2)',
  sparksCard: '0px 4px 16px rgba(0, 0, 0, 0.06)',  // Figma spec for Sparks History card
  activityCard: '0px 4px 16px rgba(0, 124, 173, 0.2)',
} as const;

// --- Responsive Typography ---

export const PROFILE_TYPOGRAPHY = {
  // "Your Activity" title: 24px (Figma fixed) — DM Sans SemiBold
  pageTitle: {
    sizeClasses: 'text-[24px] md:text-[26px] lg:text-[28px]',
    weight: 'font-semibold',
    tracking: 'tracking-[-0.96px]',
  },
  // Section title (Sparks history, Moods report): 24px DM Sans SemiBold
  sectionTitle: {
    sizeClasses: 'text-[24px] md:text-[26px] lg:text-[28px]',
    weight: 'font-semibold',
  },
  // Summary/Insights title: 18px DM Sans Bold
  insightTitle: {
    sizeClasses: 'text-[18px] md:text-[20px] lg:text-[22px]',
    weight: 'font-bold',
    tracking: 'tracking-[-0.72px]',
  },
  // Body text: 16px DM Sans Regular
  body: {
    sizeClasses: 'text-base md:text-lg lg:text-xl',
    weight: 'font-normal',
  },
  // Stat number: 20px DM Sans SemiBold
  statNumber: {
    sizeClasses: 'text-[20px] md:text-[22px] lg:text-[24px]',
    weight: 'font-semibold',
  },
  // Stat label: 12px DM Sans Black uppercase
  statLabel: {
    sizeClasses: 'text-[12px]',
    weight: 'font-black',
    extra: 'uppercase tracking-[0.72px]',
  },
  // --- Sparks History Card Typography (Figma-accurate) ---

  // Sparks card section title: 20px DM Sans Bold — Figma spec
  sparksTitle: {
    sizeClasses: 'text-[20px] md:text-[22px] lg:text-[24px]',
    weight: 'font-bold',
  },
  // Sparks card stat number: 36px DM Sans Black — Figma spec
  sparksStatNumber: {
    sizeClasses: 'text-[36px] md:text-[38px] lg:text-[40px]',
    weight: 'font-black',
  },
  // Sparks card stat label: 10px DM Sans Bold uppercase — Figma spec
  sparksStatLabel: {
    sizeClasses: 'text-[10px]',
    weight: 'font-bold',
    extra: 'uppercase tracking-[0.72px]',
  },
  // Category card name: 16px DM Sans Medium
  categoryName: {
    sizeClasses: 'text-base md:text-[17px] lg:text-lg',
    weight: 'font-medium',
  },
  // Category card description: 14px DM Sans Regular
  categoryDescription: {
    sizeClasses: 'text-sm md:text-[15px] lg:text-base',
    weight: 'font-normal',
  },
  // Badge label: 12px DM Sans Black uppercase
  badgeLabel: {
    sizeClasses: 'text-[12px]',
    weight: 'font-black',
    extra: 'uppercase tracking-[0.72px]',
  },
  // Button text: 12px DM Sans Black uppercase
  button: {
    sizeClasses: 'text-[12px]',
    weight: 'font-black',
    extra: 'uppercase tracking-[0.72px]',
  },
  // Child name on pill: 18px DM Sans Bold
  childName: {
    sizeClasses: 'text-[18px] md:text-[20px] lg:text-[22px]',
    weight: 'font-bold',
  },
  // Child age on pill: 14px DM Sans Regular
  childAge: {
    sizeClasses: 'text-sm md:text-[15px] lg:text-base',
    weight: 'font-normal',
  },
  // Category detail — date labels: 14px DM Sans Regular
  dateLabel: {
    sizeClasses: 'text-sm md:text-[15px] lg:text-base',
    weight: 'font-normal',
  },
  // Category detail — date values: 18px DM Sans Bold
  dateValue: {
    sizeClasses: 'text-[18px] md:text-[20px] lg:text-[22px]',
    weight: 'font-bold',
    tracking: 'tracking-[-0.72px]',
  },
} as const;

// --- Responsive Spacing ---

export const PROFILE_RESPONSIVE = {
  pagePaddingClasses: 'px-6 sm:px-8 md:px-10 lg:px-12',
  sectionGapClasses: 'gap-4 sm:gap-5 md:gap-6',
  elementGapClasses: 'gap-2 sm:gap-2.5 md:gap-3',
  containerMaxWidth: 'max-w-md lg:max-w-[640px]',
} as const;

// --- V1 Category Descriptions (static) ---

export const CATEGORY_DESCRIPTIONS: Record<string, Record<string, string>> = {
  language_and_communication: {
    thriving: 'Your child shows strong language and communication skills. Keep nurturing their love of conversation!',
    growing: 'Your child is developing well in language and communication. A little encouragement goes a long way!',
    building: 'Language and communication is an area where focused activities will make a big difference.',
    notAssessed: 'Take a quick check-in to discover how your child is doing in language and communication.',
  },
  personal_social_and_emotional: {
    thriving: 'Your child shows strong personal, social and emotional skills. Keep supporting their confidence!',
    growing: 'Your child is developing well personally, socially and emotionally. Keep up the great work!',
    building: 'Personal, social and emotional development is where focused activities will help the most.',
    notAssessed: 'Take a quick check-in to discover how your child is doing in personal, social and emotional development.',
  },
} as const;

// --- V1 Static Placeholder Content ---

export const STATIC_CONTENT = {
  summaryText: [
    'Sam is showing wonderful progress across key developmental areas. Their language skills are flourishing, with clear speech and growing confidence in conversations.',
    'They are also building strong social and emotional foundations, showing empathy and enthusiasm in group settings.',
    'Focus on storytelling activities and cooperative play to keep building on these strengths.',
  ],
  categoryDetailNarrative:
    'Your child demonstrates strong foundational skills in language and communication. They show confidence in expressing themselves and engage well in conversations. Their vocabulary is expanding naturally, and they are beginning to use more complex sentence structures. Continue encouraging open-ended questions and storytelling to build on these strengths.',
} as const;
